import { styled } from "@linaria/react"
import { useMemo, useState } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { DiffComponent } from "@/components/Diff/Diff.tsx"
import { Horizontal } from "@/components/Flex.ts"
import { getFullName, useMyPrincipal } from "@/hooks/useProfile.ts"
import { CodeFileRegular } from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import { buildInlineDiff } from "@/lib/diff/buildInlineDiff.ts"
import { isUserPrincipal } from "@/lib/std.ts"
import { smallerFontSize } from "@/styles/textStyles.ts"

import type { AiChatMessageContent, UUserActionType } from "@/lib/client/generated/TypeDefinitions.ts"


function getActionMessage(actionType: UUserActionType): string {
  switch (actionType.className) {
    case "RecreateEnvironment":
      return "recreated the environment"
    case "RestartEnvironment":
      return "restarted the environment"
    case "LaunchWarmupScript":
      return "launched the warmup script"
    case "StartPreviewCommand":
      return `started ${actionType.commandTitle}`
    case "UpdateWarmupScript":
      return "updated the warmup script"
    case "AddPreviewCommand":
      return `added a run script for ${actionType.title}`
    case "UpdatePreviewCommand":
      return `updated the run script for ${actionType.title}`
    case "RemovePreviewCommand":
      return `removed the run script for ${actionType.title}`
    default:
      // Fallback: use the backend's fallbackMessage property for unknown types
      return (actionType as UUserActionType).fallbackMessage
  }
}

export const UserActionMessage = ({ content, isActive, timestamp }: {
  content: AiChatMessageContent.UserAction
  isActive: boolean
  timestamp: string
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { actionType, actor } = content
  const myPrincipal = useMyPrincipal()

  const actionMessage = getActionMessage(actionType)
  const formattedTimestamp = formatDateTime(parseInt(timestamp, 10))

  let actorName = actor.name
  if (actor.id === myPrincipal.id) {
    actorName = "You"
  } else if (isUserPrincipal(actor) && actor.details.user) {
    actorName = getFullName(actor.details.user)
  }

  const scriptDiff = useMemo(() => {
    if (actionType.className === "UpdateWarmupScript") {
      return actionType.diff
    }
    if (actionType.className === "UpdatePreviewCommand") {
      return actionType.diff
    }
    if (actionType.className === "AddPreviewCommand") {
      return buildInlineDiff("", actionType.script)
    }
    if (actionType.className === "RemovePreviewCommand") {
      return buildInlineDiff(actionType.script, "")
    }
    return null
  }, [actionType])

  const messageNode = (
    <MessageContainer clickable={!!scriptDiff} onClick={scriptDiff ? () => setDialogOpen(true) : undefined}>
      <ScriptUpdatedIcon />
      {`${actorName} ${actionMessage.toLowerCase()}`}
    </MessageContainer>
  )

  return (
    <AgentMessageContainer isActive={isActive}>
      <FullWidthContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Centered>
          {scriptDiff ? (
            <Dialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              title={`${actorName} ${actionMessage.toLowerCase()}`}
              description={formattedTimestamp}
              trigger={messageNode}
            >
              <DiffComponent data={scriptDiff} panelWidth={() => "720px"} />
            </Dialog>
          ) : (
            messageNode
          )}
        </Centered>
        {isHovered && <TimestampContainer>{formattedTimestamp}</TimestampContainer>}
      </FullWidthContainer>
    </AgentMessageContainer>
  )
}

const FullWidthContainer = styled(Horizontal)`
  align-items: center;

  position: relative;
  width: 100%;
`

const MessageContainer = styled(Horizontal)<{ clickable?: boolean }>`
  align-items: center;

  ${smallerFontSize};

  color: var(--secondary-text-color);
  text-align: center;

  padding: 0 16px;
  border-radius: var(--border-radius-8);

  cursor: ${props => (props.clickable ? "pointer" : "default")};

  &:hover {
    color: ${props => (props.clickable ? "var(--text-color)" : "var(--secondary-text-color)")};
  }
`

const ScriptUpdatedIcon = styled(CodeFileRegular)`
  width: 16px;
  height: 16px;

  color: var(--text-color-40);
`

const TimestampContainer = styled.span`
  ${smallerFontSize};

  color: var(--secondary-text-color);

  position: absolute;
  right: 0;
  top: 50%;

  transform: translateY(-50%);


  background: linear-gradient(
    to right,
    transparent 0%,
    var(--background-color) 24px,
    var(--background-color) 100%
  );
`